import assert from 'assert';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  getFlowchartVersionIdForNavigation,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRun,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan,
} from 'procedures/utils/powAgnosticFlowchartHelpers';

import {
  MultiProcedureRunOrPlanListingTableQuery_study_StudyNode_activeProcedureRunsAndProcedureRunPlans_ProcedureRunNode as AccessibleProcedureRunNode,
  MultiProcedureRunOrPlanListingTableQuery_study_StudyNode_activeProcedureRunsAndProcedureRunPlans_ProcedureRunPlanNode as AccessibleProcedureRunPlanNode,
} from '../overview/__generated/MultiProcedureRunOrPlanListingTableQuery';
import type {
  ProcedureRunOverview,
  ProcedureRunPlanOverview,
} from '../overview/MultiProcedureRunAndPlanListingTable';

interface LegacyFlowchartVersionWithParentName {
  __typename: 'ProcedureFlowchartVersionNode';
  procedureFlowchart?: {
    parent?: {
      name?: string | null;
    } | null;
  } | null;
}

interface FlowchartConfigVersionWithProcedureName {
  __typename: 'ProcedureFlowchartConfigVersionNode';
  procedure?: {
    name?: string | null;
  } | null;
}

const PLACEHOLDER_PLAN_NAME = 'New run plan';

export function getProcedureRunPlanNameFromFlowchartVersion<
  T extends LegacyFlowchartVersionWithParentName | FlowchartConfigVersionWithProcedureName,
>(flowchartVersion: T | null | undefined): string {
  if (flowchartVersion == null) {
    return PLACEHOLDER_PLAN_NAME;
  }
  if (flowchartVersion.__typename === 'ProcedureFlowchartVersionNode') {
    return flowchartVersion.procedureFlowchart?.parent?.name ?? '';
  }
  if (flowchartVersion.__typename === 'ProcedureFlowchartConfigVersionNode') {
    return flowchartVersion.procedure?.name ?? '';
  }
  throw new Error('Invalid flowchart version');
}

export function getProcedureRunOverview(
  procedureRun: AccessibleProcedureRunNode
): ProcedureRunOverview | null {
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun({
    ...procedureRun,
    templateProcedureFlowchartVersion: procedureRun.runTemplateProcedureFlowchartVersion,
    procedureFlowchartConfigVersion: procedureRun.procedureFlowchartConfigVersion,
  });
  if (isInaccessible(flowchartVersion)) {
    return null;
  }
  const procedureParent = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion);
  return {
    ...procedureRun,
    procedureTemplateId: procedureParent.id,
    flowchartVersionId: getFlowchartVersionIdForNavigation(flowchartVersion),
  };
}

export function getProcedureRunPlanName(
  procedureRunPlan: Pick<
    AccessibleProcedureRunPlanNode,
    '__typename' | 'templateProcedureFlowchartVersion' | 'procedureFlowchartConfigVersion'
  >
): string {
  const flowchartOrConfigVersion =
    getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  if (flowchartOrConfigVersion == null) {
    return PLACEHOLDER_PLAN_NAME;
  }
  if (isInaccessible(flowchartOrConfigVersion)) {
    throw new Error('Expected flowchart or config version to be accessible.');
  }
  return getProcedureRunPlanNameFromFlowchartVersion(
    flowchartOrConfigVersion as LegacyFlowchartVersionWithParentName | FlowchartConfigVersionWithProcedureName
  );
}

export function getProcedureRunPlanOverview(
  procedureRunPlan: AccessibleProcedureRunPlanNode
): ProcedureRunPlanOverview | null {
  // Procedure run plans will use the template procedure flowchart version (legacy) or the procedure flowchart
  // config version (pow) to get the procedure name
  if (
    (procedureRunPlan.templateProcedureFlowchartVersion != null &&
      isInaccessible(procedureRunPlan.templateProcedureFlowchartVersion)) ||
    (procedureRunPlan.procedureFlowchartConfigVersion != null &&
      isInaccessible(procedureRunPlan.procedureFlowchartConfigVersion))
  ) {
    return null;
  }
  const flowchartOrConfigVersion =
    getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  // Using the procedure template name as the procedure run plan name until it's renamed at promotion with
  // a name template set on the study schema
  assert(
    flowchartOrConfigVersion == null || !isInaccessible(flowchartOrConfigVersion),
    'Expected flowchart or config version to be accessible.'
  );
  const procedureRunPlanName = getProcedureRunPlanNameFromFlowchartVersion(flowchartOrConfigVersion);
  return {
    id: procedureRunPlan.id,
    name: procedureRunPlanName,
    description: procedureRunPlan.description,
    createdAt: procedureRunPlan.createdAt,
  };
}
